import { useId, useState } from 'react'
import { Card } from '@/components/ui/card'
import {
  formatPercent,
  formatWaveDate,
  weekLabel,
  type CalculatedWaveWeek,
} from './model'

const SERIES = [
  {
    key: 'cumulativeWave',
    label: 'Planned waves',
    color: 'var(--color-primary)',
    dash: undefined,
  },
  {
    key: 'receiptsInStores',
    label: 'Receipts in stores',
    color: '#477b87',
    dash: '6 3',
  },
  {
    key: 'salesInStores',
    label: 'Sales in stores',
    color: '#6b7280',
    dash: '2 4',
  },
] as const

export function WavePlanningChart({
  weeks,
  setWeek,
}: {
  weeks: CalculatedWaveWeek[]
  setWeek: number
}) {
  const titleId = useId()
  const [active, setActive] = useState(
    Math.max(
      0,
      weeks.findIndex(week => week.week === setWeek)
    )
  )
  const selected = weeks[active]
  const max = Math.max(
    1,
    Math.ceil(Math.max(...weeks.map(week => week.cumulativeWave)) * 4) / 4
  )
  const left = 52,
    right = 748,
    top = 22,
    bottom = 190
  const x = (index: number) =>
    left + (index / (weeks.length - 1)) * (right - left)
  const y = (value: number) => bottom - (value / max) * (bottom - top)
  const setIndex = weeks.findIndex(week => week.week === setWeek)

  return (
    <Card className="flex min-w-0 flex-col justify-between p-5 shadow-none">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={titleId} className="text-sm font-semibold">
          Cumulative wave outlook
        </h2>
        <span className="text-xs text-muted-foreground">
          {weekLabel(5)}–{weekLabel(45)}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
        {SERIES.map(series => (
          <span
            key={series.key}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <svg width="20" height="8" aria-hidden="true">
              <line
                x1="0"
                y1="4"
                x2="20"
                y2="4"
                stroke={series.color}
                strokeWidth="2"
                strokeDasharray={series.dash}
              />
            </svg>
            {series.label}
          </span>
        ))}
      </div>
      <div
        role="slider"
        tabIndex={0}
        aria-label="Inspect wave outlook by week"
        aria-valuemin={5}
        aria-valuemax={45}
        aria-valuenow={selected.week}
        aria-valuetext={`${weekLabel(selected.week)}: planned waves ${formatPercent(selected.cumulativeWave)}, receipts in stores ${formatPercent(selected.receiptsInStores)}, sales in stores ${formatPercent(selected.salesInStores)}`}
        className="mt-2 overflow-x-auto rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onKeyDown={event => {
          const next =
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? weeks.length - 1
                : event.key === 'ArrowRight' || event.key === 'ArrowUp'
                  ? Math.min(active + 1, weeks.length - 1)
                  : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
                    ? Math.max(0, active - 1)
                    : null
          if (next !== null) {
            event.preventDefault()
            setActive(next)
          }
        }}
      >
        <svg
          viewBox="0 0 780 218"
          className="block w-full min-w-[520px]"
          aria-hidden="true"
          onPointerMove={event => {
            const bounds = event.currentTarget.getBoundingClientRect()
            const position =
              ((event.clientX - bounds.left) / bounds.width) * 780
            setActive(
              Math.max(
                0,
                Math.min(
                  weeks.length - 1,
                  Math.round(
                    ((position - left) / (right - left)) * (weeks.length - 1)
                  )
                )
              )
            )
          }}
        >
          {Array.from({ length: 5 }, (_, index) => (max * index) / 4).map(
            value => (
              <g key={value}>
                <line
                  x1={left}
                  x2={right}
                  y1={y(value)}
                  y2={y(value)}
                  stroke="var(--color-border)"
                  strokeDasharray={value === 0 ? undefined : '3 4'}
                />
                <text
                  x={left - 9}
                  y={y(value) + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="var(--color-muted-foreground)"
                >
                  {formatPercent(value, 0)}
                </text>
              </g>
            )
          )}
          {[0, 5, 10, 15, 20, 25, 30, 35, 40].map(index => (
            <text
              key={index}
              x={x(index)}
              y="210"
              textAnchor="middle"
              fontSize="10"
              fill="var(--color-muted-foreground)"
            >
              {weekLabel(weeks[index].week)}
            </text>
          ))}
          {setIndex >= 0 && (
            <g>
              <line
                x1={x(setIndex)}
                x2={x(setIndex)}
                y1={top}
                y2={bottom}
                stroke="var(--color-primary)"
                opacity="0.35"
                strokeDasharray="4 4"
              />
              <text
                x={x(setIndex)}
                y="12"
                textAnchor="middle"
                fontSize="10"
                fill="var(--color-primary)"
              >
                Set · {formatWaveDate(weeks[setIndex].date)}
              </text>
            </g>
          )}
          {SERIES.map(series => (
            <path
              key={series.key}
              data-series={series.key}
              d={weeks
                .map((week, index) =>
                  index === 0
                    ? `M${x(index)},${y(week[series.key])}`
                    : series.key === 'cumulativeWave'
                      ? `H${x(index)}V${y(week[series.key])}`
                      : `L${x(index)},${y(week[series.key])}`
                )
                .join(' ')}
              fill="none"
              stroke={series.color}
              strokeWidth="2.5"
              strokeDasharray={series.dash}
              strokeLinejoin="round"
            />
          ))}
          <line
            x1={x(active)}
            x2={x(active)}
            y1={top}
            y2={bottom}
            stroke="var(--color-muted-foreground)"
            opacity="0.4"
          />
          {SERIES.map(series => (
            <circle
              key={series.key}
              cx={x(active)}
              cy={y(selected[series.key])}
              r="3.5"
              fill={series.color}
              stroke="white"
              strokeWidth="1.5"
            />
          ))}
        </svg>
      </div>
      <div
        className="mt-2 flex flex-wrap gap-x-4 gap-y-1 rounded-md bg-secondary/70 px-3 py-2 text-xs tabular-nums"
        data-testid="wave-chart-readout"
      >
        <span className="font-semibold">
          {weekLabel(selected.week)} · {formatWaveDate(selected.date)}
        </span>
        {SERIES.map(series => (
          <span key={series.key} className="text-muted-foreground">
            {series.label}{' '}
            <strong className="font-semibold text-foreground">
              {formatPercent(selected[series.key])}
            </strong>
          </span>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Hover to inspect · Use arrow keys when the chart is focused
      </p>
    </Card>
  )
}
